// src/services/revshare.service.ts
// RevShare Service with Local Tracking
// Note: The RevShare API is for token creation, not referral tracking
// We'll implement local referral tracking with optional RevShare token creation

interface UserData {
  walletAddress: string;
  referrer?: string;
}

interface EarningsData {
  totalEarnings: number;
  pendingRewards: number;
  totalReferrals: number;
  claimedAmount: number;
  referralHistory: any[];
}

interface AnalyticsData {
  totalRevenue: number;
  tokenLaunches: number;
  activeReferrers: number;
  totalDistributed: number;
  recentTransactions: any[];
}

interface RevShareTokenRequest {
  request_id: number;
  funding_wallet: string;
  amount_to_fund: number;
  breakdown?: {
    base_cost: number;
    buy_amount: number;
    total: number;
  };
}

interface RevSharePrepareResponse {
  success: boolean;
  request_id?: number;
  funding_wallet?: string;
  amount_to_fund?: number;
  breakdown?: {
    base_cost: number;
    buy_amount: number;
    total: number;
  };
  error?: string;
}

interface RevShareCreateResponse {
  success: boolean;
  mintAddress?: string;
  data?: {
    liquidityPoolId?: string;
    [key: string]: any;
  };
  error?: string;
}

interface CreateTokenData {
  request_id: number;
  name: string;
  ticker: string;
  description: string;
  imageUrl: string;
  developerWallet: string;
  ref?: string;
  website?: string;
  twitter?: string;
  telegram?: string;
  visible?: number; // 0=visible, 1=hidden
  mode?: number; // 0=rewards, 1=jackpot, 2=lottery, 3=none
}

class RevShareService {
  private baseUrl: string = 'https://public-api.revshare.dev'; // RevShare token creation API
  private isInitialized: boolean = false;
  private currentUser: UserData | null = null;
  
  // Local storage keys for referral tracking
  private STORAGE_KEYS = {
    USERS: 'aladdin_users',
    REVENUES: 'aladdin_revenues',
    EARNINGS: 'aladdin_earnings',
    REFERRALS: 'aladdin_referrals',
    ANALYTICS: 'aladdin_analytics'
  };

  constructor() {
    this.initializeLocalStorage();
    console.log('🚀 RevShare Service initialized with local tracking');
    console.log('💡 Note: RevShare API is for token creation, not referral tracking');
  }

  private initializeLocalStorage(): void {
    // Initialize storage structures if they don't exist
    if (!localStorage.getItem(this.STORAGE_KEYS.ANALYTICS)) {
      localStorage.setItem(this.STORAGE_KEYS.ANALYTICS, JSON.stringify({
        totalRevenue: 0,
        tokenLaunches: 0,
        activeReferrers: 0,
        totalDistributed: 0,
        lastUpdated: new Date().toISOString()
      }));
    }

    // Initialize other storage keys as arrays if they don't exist
    [this.STORAGE_KEYS.USERS, this.STORAGE_KEYS.REVENUES, this.STORAGE_KEYS.REFERRALS].forEach(key => {
      if (!localStorage.getItem(key)) {
        localStorage.setItem(key, JSON.stringify([]));
      }
    });

    // Initialize earnings as an object
    if (!localStorage.getItem(this.STORAGE_KEYS.EARNINGS)) {
      localStorage.setItem(this.STORAGE_KEYS.EARNINGS, JSON.stringify({}));
    }
  }

  private getStorageData(key: string): any {
    try {
      const data = localStorage.getItem(key);
      return data ? JSON.parse(data) : null;
    } catch (error) {
      console.error('Error reading from localStorage:', error);
      return null;
    }
  }

  private setStorageData(key: string, data: any): void {
    try {
      localStorage.setItem(key, JSON.stringify(data));
    } catch (error) {
      console.error('Error writing to localStorage:', error);
    }
  }

  async initialize(walletAddress?: string): Promise<boolean> {
    try {
      // Check for referrer in URL or session storage
      const urlParams = new URLSearchParams(window.location.search);
      const referrer = urlParams.get('ref') || sessionStorage.getItem('referrer');
      
      // Store referrer in session if found
      if (referrer && !sessionStorage.getItem('referrer')) {
        sessionStorage.setItem('referrer', referrer);
        console.log('👥 Referrer detected:', referrer.slice(0, 6) + '...');
      }

      if (walletAddress) {
        this.currentUser = {
          walletAddress,
          referrer: referrer || undefined
        };

        // Register user locally
        this.registerUserLocal(walletAddress, referrer);
      }

      this.isInitialized = true;
      console.log('✅ Referral tracking initialized (local storage)');
      return true;
    } catch (error) {
      console.error('❌ Failed to initialize:', error);
      return false;
    }
  }

  private registerUserLocal(walletAddress: string, referrer?: string | null): void {
    const users = this.getStorageData(this.STORAGE_KEYS.USERS) || [];
    
    // Check if user already exists
    const existingUserIndex = users.findIndex((u: any) => u.walletAddress === walletAddress);
    
    if (existingUserIndex === -1) {
      // New user
      users.push({
        walletAddress,
        referrer,
        registeredAt: new Date().toISOString(),
        totalEarnings: 0,
        pendingRewards: 0,
        claimedAmount: 0
      });
      
      // Track referral
      if (referrer) {
        this.trackReferralLocal(referrer, walletAddress);
      }
      
      console.log('✅ New user registered:', walletAddress.slice(0, 6) + '...');
    } else {
      // Update existing user
      if (referrer && !users[existingUserIndex].referrer) {
        users[existingUserIndex].referrer = referrer;
        this.trackReferralLocal(referrer, walletAddress);
      }
    }
    
    this.setStorageData(this.STORAGE_KEYS.USERS, users);
  }

  private trackReferralLocal(referrer: string, referred: string): void {
    const referrals = this.getStorageData(this.STORAGE_KEYS.REFERRALS) || [];
    
    // Check if this referral already exists
    const exists = referrals.some((r: any) => 
      r.referrer === referrer && r.referred === referred
    );
    
    if (!exists) {
      referrals.push({
        referrer,
        referred,
        timestamp: new Date().toISOString()
      });
      
      this.setStorageData(this.STORAGE_KEYS.REFERRALS, referrals);
      console.log('👥 Referral tracked:', referrer.slice(0, 6) + '... referred', referred.slice(0, 6) + '...');
    }
  }

  async registerUser(walletAddress: string, referrer?: string | null): Promise<boolean> {
    this.registerUserLocal(walletAddress, referrer);
    return true;
  }

  async trackRevenue(amount: number, type: string, metadata?: any): Promise<boolean> {
    try {
      if (!this.currentUser) {
        console.warn('⚠️ No user connected, cannot track revenue');
        return false;
      }

      // Track revenue locally
      const revenues = this.getStorageData(this.STORAGE_KEYS.REVENUES) || [];
      const revenue = {
        walletAddress: this.currentUser.walletAddress,
        amount,
        type,
        referrer: this.currentUser.referrer,
        metadata,
        timestamp: new Date().toISOString()
      };
      
      revenues.push(revenue);
      this.setStorageData(this.STORAGE_KEYS.REVENUES, revenues);

      // Calculate and distribute commission if there's a referrer
      if (this.currentUser.referrer) {
        const commission = amount * 0.2; // 20% commission
        this.distributeCommissionLocal(this.currentUser.referrer, commission);
        console.log(`💸 Commission distributed: ${commission.toFixed(4)} SOL to referrer`);
      }

      // Update analytics
      this.updateAnalyticsLocal(amount, type);

      console.log(`💰 Revenue tracked: ${amount} SOL for ${type}`);
      return true;
    } catch (error) {
      console.error('❌ Failed to track revenue:', error);
      return false;
    }
  }

  private distributeCommissionLocal(referrer: string, commission: number): void {
    // Update earnings
    const earnings = this.getStorageData(this.STORAGE_KEYS.EARNINGS) || {};
    
    if (!earnings[referrer]) {
      earnings[referrer] = {
        totalEarnings: 0,
        pendingRewards: 0,
        claimedAmount: 0
      };
    }
    
    earnings[referrer].totalEarnings += commission;
    earnings[referrer].pendingRewards += commission;
    
    this.setStorageData(this.STORAGE_KEYS.EARNINGS, earnings);

    // Update user record
    const users = this.getStorageData(this.STORAGE_KEYS.USERS) || [];
    const userIndex = users.findIndex((u: any) => u.walletAddress === referrer);
    
    if (userIndex !== -1) {
      users[userIndex].totalEarnings = (users[userIndex].totalEarnings || 0) + commission;
      users[userIndex].pendingRewards = (users[userIndex].pendingRewards || 0) + commission;
      this.setStorageData(this.STORAGE_KEYS.USERS, users);
    }
  }

  private updateAnalyticsLocal(amount: number, type: string): void {
    const analytics = this.getStorageData(this.STORAGE_KEYS.ANALYTICS) || {};
    
    analytics.totalRevenue = (analytics.totalRevenue || 0) + amount;
    
    if (type === 'token_launch') {
      analytics.tokenLaunches = (analytics.tokenLaunches || 0) + 1;
    }
    
    // Calculate active referrers
    const referrals = this.getStorageData(this.STORAGE_KEYS.REFERRALS) || [];
    const uniqueReferrers = new Set(referrals.map((r: any) => r.referrer));
    analytics.activeReferrers = uniqueReferrers.size;
    
    // Calculate total distributed
    const earnings = this.getStorageData(this.STORAGE_KEYS.EARNINGS) || {};
    analytics.totalDistributed = Object.values(earnings).reduce((sum: number, e: any) => 
      sum + (e.totalEarnings || 0), 0
    );
    
    analytics.lastUpdated = new Date().toISOString();
    
    this.setStorageData(this.STORAGE_KEYS.ANALYTICS, analytics);
  }

  async getUserEarnings(walletAddress?: string): Promise<EarningsData> {
    const address = walletAddress || this.currentUser?.walletAddress;
    
    if (!address) {
      return {
        totalEarnings: 0,
        pendingRewards: 0,
        totalReferrals: 0,
        claimedAmount: 0,
        referralHistory: []
      };
    }

    // Get earnings from local storage
    const earnings = this.getStorageData(this.STORAGE_KEYS.EARNINGS) || {};
    const userEarnings = earnings[address] || {
      totalEarnings: 0,
      pendingRewards: 0,
      claimedAmount: 0
    };

    // Get referrals
    const referrals = this.getStorageData(this.STORAGE_KEYS.REFERRALS) || [];
    const userReferrals = referrals.filter((r: any) => r.referrer === address);

    // Get referral history with earnings
    const referralHistory = userReferrals.map((r: any) => ({
      walletAddress: r.referred,
      timestamp: r.timestamp,
      earnings: this.calculateReferralEarnings(r.referred)
    }));

    return {
      totalEarnings: userEarnings.totalEarnings || 0,
      pendingRewards: userEarnings.pendingRewards || 0,
      totalReferrals: userReferrals.length,
      claimedAmount: userEarnings.claimedAmount || 0,
      referralHistory
    };
  }

  private calculateReferralEarnings(referredWallet: string): number {
    const revenues = this.getStorageData(this.STORAGE_KEYS.REVENUES) || [];
    const referredRevenues = revenues.filter((r: any) => r.walletAddress === referredWallet);
    const totalRevenue = referredRevenues.reduce((sum: number, r: any) => sum + r.amount, 0);
    return totalRevenue * 0.2; // 20% commission
  }

  async claimEarnings(): Promise<{ success: boolean; amount: number; transactionHash?: string }> {
    try {
      if (!this.currentUser) {
        throw new Error('No user connected');
      }

      const earnings = this.getStorageData(this.STORAGE_KEYS.EARNINGS) || {};
      const userEarnings = earnings[this.currentUser.walletAddress];

      if (!userEarnings || userEarnings.pendingRewards === 0) {
        return {
          success: false,
          amount: 0
        };
      }

      const amountToClaim = userEarnings.pendingRewards;

      // Update earnings (move pending to claimed)
      userEarnings.claimedAmount = (userEarnings.claimedAmount || 0) + amountToClaim;
      userEarnings.pendingRewards = 0;
      earnings[this.currentUser.walletAddress] = userEarnings;
      this.setStorageData(this.STORAGE_KEYS.EARNINGS, earnings);

      // Update user record
      const users = this.getStorageData(this.STORAGE_KEYS.USERS) || [];
      const userIndex = users.findIndex((u: any) => u.walletAddress === this.currentUser!.walletAddress);
      
      if (userIndex !== -1) {
        users[userIndex].pendingRewards = 0;
        users[userIndex].claimedAmount = (users[userIndex].claimedAmount || 0) + amountToClaim;
        this.setStorageData(this.STORAGE_KEYS.USERS, users);
      }

      console.log(`✅ Successfully claimed ${amountToClaim.toFixed(4)} SOL (simulated)`);
      console.log('⚠️ Note: Actual SOL transfer requires smart contract integration');

      return {
        success: true,
        amount: amountToClaim,
        transactionHash: '0x' + Math.random().toString(36).substring(2, 15) // Simulated
      };
    } catch (error) {
      console.error('❌ Failed to claim earnings:', error);
      return {
        success: false,
        amount: 0
      };
    }
  }

  async getAnalytics(): Promise<AnalyticsData> {
    const analytics = this.getStorageData(this.STORAGE_KEYS.ANALYTICS) || {
      totalRevenue: 0,
      tokenLaunches: 0,
      activeReferrers: 0,
      totalDistributed: 0
    };

    // Get recent transactions
    const revenues = this.getStorageData(this.STORAGE_KEYS.REVENUES) || [];
    const recentTransactions = revenues
      .slice(-10)
      .reverse()
      .map((r: any) => ({
        ...r,
        walletAddress: r.walletAddress.slice(0, 6) + '...' + r.walletAddress.slice(-4)
      }));

    return {
      ...analytics,
      recentTransactions
    };
  }

  async prepareTokenCreation(buyAmount?: number): Promise<RevSharePrepareResponse> {
    try {
      let url = `${this.baseUrl}/prepare`;
      if (buyAmount && buyAmount > 0) {
        url += `?buy_amount=${buyAmount}`;
      }
      
      const response = await fetch(url, {
        method: 'GET'
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const data = await response.json();
      
      console.log('🎯 Token preparation response:', data);
      
      if (data.success) {
        console.log('💰 Send', data.amount_to_fund, 'SOL to', data.funding_wallet);
        
        if (data.breakdown) {
          console.log('Cost breakdown:', data.breakdown);
        }
        
        return {
          success: true,
          request_id: data.request_id,
          funding_wallet: data.funding_wallet,
          amount_to_fund: data.amount_to_fund,
          breakdown: data.breakdown
        };
      } else {
        return {
          success: false,
          error: data.error || 'Failed to prepare token creation'
        };
      }
    } catch (error) {
      console.error('❌ Failed to prepare token creation:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  async createRevShareToken(tokenData: CreateTokenData): Promise<RevShareCreateResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(tokenData)
      });

      const data = await response.json();
      
      if (data.success) {
        console.log('🎉 RevShare token created successfully!');
        console.log('🪙 Token address:', data.mintAddress);
        console.log('🏊 Liquidity pool:', data.data?.liquidityPoolId);
        return {
          success: true,
          mintAddress: data.mintAddress,
          data: data.data
        };
      } else {
        return {
          success: false,
          error: data.error || 'Token creation failed'
        };
      }
    } catch (error) {
      console.error('❌ Failed to create RevShare token:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  // Helper method to convert mode string to number
  getModeValue(mode: 'rewards' | 'jackpot' | 'lottery' | 'none'): number {
    const modeMap = {
      'rewards': 0,
      'jackpot': 1,
      'lottery': 2,
      'none': 3
    };
    return modeMap[mode] || 0;
  }

  // Helper methods
  generateReferralLink(walletAddress: string): string {
    const baseUrl = window.location.origin;
    return `${baseUrl}?ref=${walletAddress}`;
  }

  getCurrentUser(): UserData | null {
    return this.currentUser;
  }

  setCurrentUser(walletAddress: string, referrer?: string): void {
    this.currentUser = {
      walletAddress,
      referrer
    };
    console.log('👤 Current user updated:', walletAddress.slice(0, 6) + '...');
  }

  clearUser(): void {
    this.currentUser = null;
    console.log('👤 User data cleared');
  }

  isServiceInitialized(): boolean {
    return this.isInitialized;
  }

  // Export/Import data methods for backup
  exportData(): string {
    const data = {
      users: this.getStorageData(this.STORAGE_KEYS.USERS),
      revenues: this.getStorageData(this.STORAGE_KEYS.REVENUES),
      earnings: this.getStorageData(this.STORAGE_KEYS.EARNINGS),
      referrals: this.getStorageData(this.STORAGE_KEYS.REFERRALS),
      analytics: this.getStorageData(this.STORAGE_KEYS.ANALYTICS),
      exportedAt: new Date().toISOString()
    };
    return JSON.stringify(data, null, 2);
  }

  importData(jsonData: string): boolean {
    try {
      const data = JSON.parse(jsonData);
      
      if (data.users) this.setStorageData(this.STORAGE_KEYS.USERS, data.users);
      if (data.revenues) this.setStorageData(this.STORAGE_KEYS.REVENUES, data.revenues);
      if (data.earnings) this.setStorageData(this.STORAGE_KEYS.EARNINGS, data.earnings);
      if (data.referrals) this.setStorageData(this.STORAGE_KEYS.REFERRALS, data.referrals);
      if (data.analytics) this.setStorageData(this.STORAGE_KEYS.ANALYTICS, data.analytics);
      
      console.log('✅ Data imported successfully');
      return true;
    } catch (error) {
      console.error('❌ Failed to import data:', error);
      return false;
    }
  }
}

export default new RevShareService();